import {
  AsyncRemoteWidgetAMDModuleProvider,
  RemoteMetaDataProvider,
  BundledMetaDataProvider,
  RemoteWidgetModuleProvider,
  RemoteWidgetsLoader,
  BundledWidgetModuleProvider,
  isBlocksAppParams,
  ModuleProviderType,
  WidgetBundleProvider,
} from '@wix/blocks-widget-services';
import {
  BlocksAppParams,
  BlocksConfig,
  WidgetControllerConfig,
} from '@wix/blocks-widget-services-types';
import { ILogger } from '@wix/blocks-widget-services/common';

const LEGACY_REVISION_PREFIX = 'legacy';

const isLegacyBlocksApp = (
  appParams: WidgetControllerConfig['appParams'],
): appParams is BlocksAppParams =>
  !appParams.baseUrls?.siteAssets && !!appParams.blocksData;

const createBlocksConfigFromLegacyAppData = (
  appParams: BlocksAppParams,
  metaSiteId: string,
): BlocksConfig => {
  const { wixCodeInstanceId, wixCodeGridId } = appParams.blocksData;

  return {
    appDefinitionId: appParams.appDefinitionId,
    code: {
      instanceId: wixCodeInstanceId,
      gridId: wixCodeGridId,
      experimentsQueryString:
        appParams.appData?.blocksConsumerData?.codeExperimentsQueryString || '', // Should be replaced with a type guard once WBL-2343 is done
    },
    metaSiteId,
    revision: `${LEGACY_REVISION_PREFIX}-${wixCodeGridId}`,
  };
};

const createLegacyRemoteWidgetsLoader = (
  controllersConfigs: WidgetControllerConfig[],
  appParams: BlocksAppParams,
  metaSiteId: string,
  {
    logger,
  }: {
    logger: ILogger;
  },
) => {
  const blocksConfig = createBlocksConfigFromLegacyAppData(
    appParams,
    metaSiteId,
  );

  const remoteMetaDataProvider = new RemoteMetaDataProvider(
    appParams,
    controllersConfigs,
    { logger, blocksConfig },
  );

  const remoteModuleProvider = createModuleProvider(
    controllersConfigs[0],
    blocksConfig,
  );

  return new RemoteWidgetsLoader(
    appParams,
    remoteMetaDataProvider,
    remoteModuleProvider,
  );
};

const createProviders = (
  appParams: WidgetControllerConfig['appParams'],
  controllerConfigs: WidgetControllerConfig[],
  options?: {
    logger?: ILogger;
  },
) => {
  const moduleProviderType = getModuleProviderType(controllerConfigs[0]);
  if (moduleProviderType === ModuleProviderType.BundledCodeModuleProvider) {
    const bundleLoader = new WidgetBundleProvider(controllerConfigs[0]);
    return {
      metaDataProvider: new BundledMetaDataProvider(bundleLoader),
      moduleProvider: new BundledWidgetModuleProvider(
        bundleLoader,
        controllerConfigs[0],
      ),
    };
  }

  return {
    metaDataProvider: new RemoteMetaDataProvider(appParams, controllerConfigs, {
      logger: options?.logger,
    }),
    moduleProvider: createModuleProvider(controllerConfigs[0]),
  };
};

const createModuleProvider = (
  controllerConfig: WidgetControllerConfig,
  blocksConfig?: BlocksConfig,
) => {
  const moduleProviderType = getModuleProviderType(controllerConfig);

  return moduleProviderType === ModuleProviderType.AMDModuleProvider
    ? new AsyncRemoteWidgetAMDModuleProvider(controllerConfig, {
        blocksConfig,
      })
    : new RemoteWidgetModuleProvider(controllerConfig.appParams, {
        blocksConfig,
      });
};

const getModuleProviderType = (
  controllerConfig: WidgetControllerConfig,
): ModuleProviderType => {
  if (
    isBlocksAppParams(controllerConfig.appParams) &&
    controllerConfig.appParams.appData?.blocksConsumerData.widgetBundleUrls
  ) {
    return ModuleProviderType.BundledCodeModuleProvider;
  }

  if (controllerConfig.wixCodeApi.environment?.network.importAMDModule) {
    return ModuleProviderType.AMDModuleProvider;
  }

  return ModuleProviderType.DefaultModuleProvider;
};

export const createRemoteWidgetsLoader = (
  controllersConfigs: WidgetControllerConfig[],
  {
    logger,
  }: {
    logger: ILogger;
  },
) => {
  const { appParams, platformAPIs } = controllersConfigs[0];

  if (isLegacyBlocksApp(appParams) && platformAPIs.bi?.metaSiteId) {
    console.info('Loading legacy blocks app');
    return createLegacyRemoteWidgetsLoader(
      controllersConfigs,
      appParams,
      platformAPIs.bi.metaSiteId,
      {
        logger,
      },
    );
  }

  const { metaDataProvider, moduleProvider } = createProviders(
    appParams,
    controllersConfigs,
    {
      logger,
    },
  );

  return new RemoteWidgetsLoader(appParams, metaDataProvider, moduleProvider);
};
